import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import WeekPlanSummary from '../meal-prep/WeekPlanSummary'
import { fmt, fmtWeekday } from '../meal-prep/planFormat'
import { errorCode, formatDate } from './cycleStatus'
import { uiLanguage, usePlanPreview, useWeekPlanning } from './useWeekPlanning'
import WeekPlanEmptyState from './WeekPlanEmptyState'
import WeekPlanPreview from './WeekPlanPreview'

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]
const addDays = (iso, days) => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function Totals({ label, main, sub, testId }) {
  return (
    <div className="flex flex-col min-w-0" data-testid={testId}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 tabular-nums">{main}</span>
      {sub && <span className="text-xs text-gray-500 tabular-nums">{sub}</span>}
    </div>
  )
}

// VH-229 refinement: one cycle week — dates, planned vs logged, and (expanded) the plan itself.
export function CycleWeekPanel({ cycleId, week, defaultOpen = false }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(defaultOpen)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [error, setError] = useState(null)
  const onError = (err) => setError(t(`cycleReports.errors.${errorCode(err)}`, t('healthCycle.errors.generic')))
  const planning = useWeekPlanning({ cycleId, week, onError })
  const previewQuery = usePlanPreview(cycleId, week.plan_id, open && !!week.preview_state)
  const preview = previewQuery.data
  const language = uiLanguage(lang)

  const remove = useMutation({
    mutationFn: () => healthCyclesAPI.deleteWeek(cycleId, week.plan_id),
    onSuccess: () => {
      setConfirmDelete(false)
      queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks', cycleId] })
    },
    onError,
  })
  const restDay = useMutation({
    mutationFn: (weekday) => healthCyclesAPI.setRestDay(cycleId, week.plan_id, weekday),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks', cycleId] })
      queryClient.invalidateQueries({ queryKey: ['planDocument', cycleId, week.plan_id] })
    },
    onError,
  })

  const range = `${formatDate(week.week_start, lang)} – ${formatDate(week.week_end, lang)}`
  if (!week.plan_id) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-500" data-testid={`cycle-week-${week.week_start}`}>
        <span className="font-medium text-gray-700">{range}</span> · {t('cycleReports.weekStatus.no_plan', 'no_plan')}
      </div>
    )
  }

  const planned = week.planned ?? {}
  const logged = week.logged ?? {}
  const plannedMain = !week.has_document
    ? t('weekPlanning.noPlan')
    : planned.kcal != null
    ? t('cycleReports.nutrition.perWeek', { kcal: fmt(planned.kcal, lang), protein: fmt(planned.protein_g, lang) })
    : '—'
  const plannedSub = week.has_document && planned.kcal_per_day != null
    ? t('cycleReports.nutrition.perContainerDay', { kcal: fmt(planned.kcal_per_day, lang), protein: fmt(planned.protein_g_per_day, lang) })
    : null
  const loggedMain = logged.logged_days > 0
    ? t('cycleReports.nutrition.loggedTotals', { kcal: fmt(logged.kcal, lang), protein: fmt(logged.protein_g, lang) })
    : '—'
  const loggedSub = t('cycleReports.nutrition.loggedDays', { count: logged.logged_days ?? 0 })

  return (
    <article className={`rounded-lg border ${week.is_current ? 'border-purple-300' : 'border-gray-200'} bg-surface`} data-testid={`cycle-week-${week.week_start}`}>
      <button type="button" className="w-full text-left px-4 py-3 grid gap-3 grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,1.2fr)_auto] items-start"
        aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid={`cycle-week-toggle-${week.plan_id}`}>
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900">{range}</span>
          <span className="flex flex-wrap items-center gap-1.5 text-xs">
            {week.is_current && <span className="rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-800">{t('cycleReports.nutrition.currentWeek')}</span>}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">{t(`cycleReports.weekStatus.${week.status}`, week.status)}</span>
            {week.locked && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">{t('cycleReports.nutrition.locked')}</span>}
          </span>
        </div>
        <Totals label={t('cycleReports.nutrition.planned')} main={plannedMain} sub={plannedSub} testId={`cycle-week-planned-${week.plan_id}`} />
        <Totals label={t('cycleReports.nutrition.logged')} main={loggedMain} sub={loggedSub} testId={`cycle-week-logged-${week.plan_id}`} />
        <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform justify-self-end ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="border-t border-gray-200 px-4 py-4 space-y-4" data-testid={`cycle-week-body-${week.plan_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
              <span className="font-medium">{t('cycleReports.nutrition.restDay')}</span>
              <select className="form-input py-1 text-sm w-auto" value={week.rest_day ?? ''} disabled={week.locked || restDay.isPending}
                onChange={(e) => restDay.mutate(Number(e.target.value))} data-testid={`rest-day-${week.plan_id}`}>
                {week.rest_day == null && <option value="">—</option>}
                {WEEKDAYS.map((d) => <option key={d} value={d}>{fmtWeekday(addDays(week.week_start, d - 1), lang)}</option>)}
              </select>
              {week.rest_day_source && (
                <span className="text-xs text-gray-500">{t(`cycleReports.nutrition.restDaySource.${week.rest_day_source}`)}</span>
              )}
            </label>
            <span className="flex flex-wrap gap-2">
              {week.has_document && !preview && !week.locked && (
                <>
                  <button type="button" className="btn-outline text-sm" disabled={planning.adjust.isPending}
                    onClick={() => { setError(null); planning.adjust.mutate() }} data-testid={`adjust-week-${week.plan_id}`}>
                    {t('weekPlanning.actions.adjust')}
                  </button>
                  <button type="button" className="btn-outline text-sm" disabled={planning.generate.isPending}
                    onClick={() => { setError(null); planning.generate.mutate({ language }) }} data-testid={`regenerate-week-${week.plan_id}`}>
                    {t('weekPlanning.actions.regenerate')}
                  </button>
                  {week.previous_document_week && (
                    <button type="button" className="btn-outline text-sm" onClick={() => setConfirmReplace(true)}>
                      {t('weekPlanning.copy.action')}
                    </button>
                  )}
                </>
              )}
              {week.deletable && (
                <button type="button" className="btn-outline-danger text-sm" onClick={() => setConfirmDelete(true)}
                  data-testid={`delete-week-${week.plan_id}`}>
                  {t('cycleReports.nutrition.deleteWeek')}
                </button>
              )}
            </span>
          </div>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          {preview && <WeekPlanPreview preview={preview} hasDocument={week.has_document} actions={planning} language={language} />}
          {!preview && !week.has_document && !previewQuery.isLoading && (
            <WeekPlanEmptyState previousWeek={week.previous_document_week}
              copying={planning.copyPrevious.isPending} generating={planning.generate.isPending}
              onCopy={() => { setError(null); planning.copyPrevious.mutate(false) }}
              onGenerate={(wishes) => { setError(null); planning.generate.mutate({ language, instruction: wishes?.trim() || null }) }} />
          )}
          {week.has_document && <WeekPlanSummary cycleId={cycleId} planId={week.plan_id} weekStart={week.week_start} />}
        </div>
      )}

      <Dialog open={confirmReplace} onClose={() => setConfirmReplace(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-surface-container p-6 shadow-xl">
            <DialogTitle className="text-lg font-semibold text-gray-900">{t('weekPlanning.copy.replaceTitle', { range })}</DialogTitle>
            <p className="mt-3 text-sm text-gray-700">
              {t('weekPlanning.copy.replaceBody', { date: formatDate(week.previous_document_week, lang) })}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="btn-outline" onClick={() => setConfirmReplace(false)}>{t('common.cancel')}</button>
              <button type="button" className="btn-danger" disabled={planning.copyPrevious.isPending}
                onClick={() => planning.copyPrevious.mutate(true, { onSuccess: () => setConfirmReplace(false) })}>
                {t('weekPlanning.copy.replaceConfirm')}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-surface-container p-6 shadow-xl">
            <DialogTitle className="text-lg font-semibold text-gray-900">{t('cycleReports.nutrition.deleteTitle', { range })}</DialogTitle>
            <p className="mt-3 text-sm text-gray-700">{t('cycleReports.nutrition.deleteBody')}</p>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="btn-outline" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
              <button type="button" className="btn-danger" disabled={remove.isPending}
                onClick={() => remove.mutate()} data-testid="confirm-delete-week">
                {remove.isPending ? t('cycleReports.nutrition.deleting') : t('cycleReports.nutrition.deleteConfirm')}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </article>
  )
}

export default CycleWeekPanel
